import React, {useState} from 'react';
import styled from 'styled-components';
import {Alert, Button, TextField} from '@betareader/design-system';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {createReader, updateReader} from '../../modules/reader';
import {authUtil, userProfileUtil} from '../../utils';

const Form = styled.form`
  display: grid;
  gap: var(--br-space-3);
`;

const VersionField = styled.label`
  display: grid;
  gap: var(--br-space-2);
`;

const Select = styled.select`
  background: var(--br-surface);
  border: 1px solid var(--br-border);
  border-radius: var(--br-control-radius);
  color: var(--br-text);
  min-height: 40px;
  padding: 0 var(--br-space-2);
`;

const InvitationForm = ({reader, invitationSent, disabled}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const currentBook = useSelector(state => state.book.currentBook);
  const updatingReader = useSelector(state => state.reader.updatingReader);
  const [userEmail, setUserEmail] = useState(reader?.invitation?.email ?? '');
  const [contentVersion, setContentVersion] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const versions = currentBook?.content || [];
  const validEmail = userProfileUtil.validateEmail(userEmail);
  const busy = submitting || Boolean(updatingReader);

  const submit = async event => {
    event.preventDefault();
    if (disabled || busy || !validEmail || !currentBook?._id) return;
    setError(null);
    setSubmitting(true);
    try {
      const idToken = await authUtil.getFreshIdToken();
      const complete = success => {
        setSubmitting(false);
        if (success) {
          invitationSent?.();
        } else {
          setError(t('SomethingWentWrong'));
        }
      };
      if (reader) {
        const data = {'invitation.email': userEmail};
        if (contentVersion) data.contentVersion = contentVersion;
        dispatch(updateReader(idToken, currentBook._id, reader._id, data, complete));
      } else {
        const version = contentVersion || versions[versions.length - 1]?._id;
        if (!version) {
          setSubmitting(false);
          setError(t('SomethingWentWrong'));
          return;
        }
        dispatch(createReader(idToken, {
          bookId: currentBook._id,
          contentVersion: version,
          userEmail,
          sendMail: true,
          source: 'book',
        }, complete));
      }
    } catch (cause) {
      setSubmitting(false);
      setError(cause.message || t('SomethingWentWrong'));
    }
  };

  return (
    <Form onSubmit={submit}>
      <TextField id='reader-invitation-email' type='email' label={t('Email')}
        disabled={disabled || busy} placeholder='email@example.com'
        value={userEmail} onChange={event => setUserEmail(event.target.value)} />
      {versions.length > 1 && (
        <VersionField htmlFor='reader-invitation-version'>
          <span>{t('Version')}</span>
          <Select id='reader-invitation-version' value={contentVersion}
            disabled={disabled || busy}
            onChange={event => setContentVersion(event.target.value)}>
            <option value=''>{t('latestVersion')}</option>
            {versions.map(version => (
              <option key={version._id} value={version._id}>
                v{version.versionNumber || 1}
              </option>
            ))}
          </Select>
        </VersionField>
      )}
      {error && <Alert variant='error'>{error}</Alert>}
      <Button type='submit' disabled={disabled || busy || !validEmail || !currentBook?._id}
        aria-busy={busy}>
        {busy ? t('Sending') : t('Invite')}
      </Button>
    </Form>
  );
};

export default InvitationForm;
